import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import gsap from "gsap";

/** Institutional cursor for marketing pages — neutral black/grey, no purple */
export function PublicCursor() {
  const [location] = useLocation();
  const cursorRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [cursorText, setCursorText] = useState("");

  const isExcluded = ["/login", "/dashboard", "/admin", "/superadmin"].some(
    (p) => location === p || location.startsWith(`${p}/`)
  );

  useEffect(() => {
    if (isExcluded) return;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (prefersReduced || isTouch) return;

    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return;

    let mouseX = window.innerWidth / 2;
    let mouseY = window.innerHeight / 2;

    const move = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      gsap.to(dot, { x: mouseX, y: mouseY, duration: 0.12, ease: "power2.out" });
      gsap.to(ring, { x: mouseX, y: mouseY, duration: 0.28, ease: "power2.out" });
    };

    const onEnter = (e: Event) => {
      const target = e.target as HTMLElement;
      setIsHovering(true);
      const label = target.dataset.cursorLabel;
      if (label) setCursorText(label);
      gsap.to(ring, { scale: 1.4, duration: 0.2 });
      gsap.to(dot, { scale: 0.6, duration: 0.2 });
    };

    const onLeave = () => {
      setIsHovering(false);
      setCursorText("");
      gsap.to(ring, { scale: 1, duration: 0.2 });
      gsap.to(dot, { scale: 1, duration: 0.2 });
    };

    const bindInteractive = () => {
      document.querySelectorAll("a, button, [data-cursor-hover], input, textarea, select").forEach((el) => {
        el.removeEventListener("mouseenter", onEnter);
        el.removeEventListener("mouseleave", onLeave);
        el.addEventListener("mouseenter", onEnter);
        el.addEventListener("mouseleave", onLeave);
      });
    };

    document.addEventListener("mousemove", move);
    bindInteractive();
    const observer = new MutationObserver(bindInteractive);
    observer.observe(document.body, { childList: true, subtree: true });
    document.body.style.cursor = "none";

    return () => {
      document.removeEventListener("mousemove", move);
      observer.disconnect();
      document.body.style.cursor = "auto";
    };
  }, [isExcluded, location]);

  if (isExcluded) return null;
  if (typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0)) {
    return null;
  }

  return (
    <div ref={cursorRef} className="pointer-events-none fixed inset-0 z-[9999] hidden lg:block">
      <div
        ref={dotRef}
        className="fixed top-0 left-0 w-1.5 h-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-foreground"
        style={{ transform: "translate(-50%, -50%)" }}
      />
      <div
        ref={ringRef}
        className="fixed top-0 left-0 w-9 h-9 -translate-x-1/2 -translate-y-1/2 rounded-full border border-foreground/25 bg-transparent transition-colors"
        style={{ transform: "translate(-50%, -50%)" }}
      >
        {cursorText && (
          <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[9px] font-medium whitespace-nowrap text-foreground/80">
            {cursorText}
          </span>
        )}
      </div>
    </div>
  );
}
